// A task row's pieces shared across the task surfaces (#11942): the mark at a
// row's head and the path of a list.
import React from "react";
import { CheckIcon, MinusIcon } from "@phosphor-icons/react";
import { isAgents } from "./store";
import type { Task } from "./types";

export const listPath = (listId: string): string =>
  `/tasks/lists/${encodeURIComponent(listId)}`;

/** The mark at the row's head: a filled check when done, a dashed box when
 * skipped, an empty circle otherwise. For the viewer's own plain task the
 * circle completes it in place. */
export const TaskMark: React.FC<{
  task: Task;
  actionable: boolean;
  current?: boolean;
  /** The mark ticks and unticks through this; without it the mark only
   * shows the state. */
  onToggle?: () => void;
}> = ({ task, actionable, current = false, onToggle }) => {
  if (task.status === "done")
    return onToggle && actionable ? (
      <button
        type="button"
        aria-label={`Reopen: ${task.title}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-[var(--fp-st-active)] text-white"
      >
        <CheckIcon size={11} weight="bold" />
      </button>
    ) : (
      <span className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-[var(--fp-st-active)] text-white">
        <CheckIcon size={11} weight="bold" />
      </span>
    );
  if (task.status === "skipped")
    return (
      <span className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px] border-[1.5px] border-dashed border-[#c9ccd1] text-[var(--fp-ink-4)]">
        <MinusIcon size={10} weight="bold" />
      </span>
    );
  // The agent's task under way: a dashed accent ring, the spec's "working"
  // mark, distinct from the plain circle of a task nobody has started.
  if (task.status === "in_progress" && isAgents(task))
    return (
      <span
        aria-label={`${task.title} · working`}
        className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-[1.5px] border-dashed border-[var(--fp-accent)] bg-white"
      />
    );
  const completes = actionable && task.action === "mark_done" && !!onToggle;
  return (
    <button
      type="button"
      aria-label={completes ? `Mark done: ${task.title}` : task.title}
      disabled={!completes}
      onClick={(e) => {
        e.stopPropagation();
        if (!completes || !onToggle) return;
        onToggle();
      }}
      className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-[1.5px] bg-white transition-colors ${
        current ? "border-[var(--fp-accent)]" : "border-[#c9ccd1]"
      } ${completes ? "cursor-pointer hover:border-[var(--fp-ink)]" : "cursor-default"}`}
    />
  );
};
